import { cloneElement, isValidElement, type CSSProperties, type ReactElement, type ReactNode } from "react";
import { alpha, useTheme } from "@mui/material";
import { PdfIcon } from "../../assets/custom-icons/pdf-icon";
import { WordIcon } from "../../assets/custom-icons/word-icon";
import { ExcelIcon } from "../../assets/custom-icons/excel-icon";

/**
 * Inline SVG icon set for the Bestek AI redesign, ported 1:1 from the Claude
 * Design project (.design-ref/dashboard.dc.html). Icons stroke with
 * `currentColor` so they inherit the surrounding text color.
 */

export type IconName =
  | "dashboard"
  | "convert"
  | "grid"
  | "sparkles"
  | "results"
  | "users"
  | "building"
  | "lock"
  | "bars"
  | "message"
  | "filetext"
  | "sliders"
  | "warning"
  | "summarize"
  | "find"
  | "qa"
  | "factcheck"
  | "compare"
  | "swap"
  | "search"
  | "download"
  | "trash"
  | "close"
  | "refresh"
  | "clock"
  | "plus"
  | "chevronDown"
  | "chevronRight"
  | "arrowLeft"
  | "arrowRight"
  | "arrowUpRight"
  | "check"
  | "checkCircle"
  | "upload"
  | "edit"
  | "settings"
  | "info"
  | "play"
  | "logout"
  | "folder"
  | "user"
  | "calendar"
  | "language"
  | "receipt"
  | "fileConvert"
  | "fileCheck02"
  | "convert02"
  | "alertTriangle"
  | "fileList";

const PATHS: Record<IconName, ReactNode> = {
  dashboard: (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <g clip-path="url(#clip0_5877_5806)">
        <path d="M4.16797 10.0001C4.16797 6.77842 6.77964 4.16675 10.0013 4.16675M13.7512 6.25008L10.0012 10.0001M18.3346 10.0001C18.3346 14.6025 14.6037 18.3334 10.0013 18.3334C5.39893 18.3334 1.66797 14.6025 1.66797 10.0001C1.66797 5.39771 5.39893 1.66675 10.0013 1.66675C14.6037 1.66675 18.3346 5.39771 18.3346 10.0001ZM10.8346 10.0001C10.8346 10.4603 10.4615 10.8334 10.0013 10.8334C9.54106 10.8334 9.16797 10.4603 9.16797 10.0001C9.16797 9.53984 9.54106 9.16675 10.0013 9.16675C10.4615 9.16675 10.8346 9.53984 10.8346 10.0001Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </g>
      <defs>
        <clipPath id="clip0_5877_5806">
          <rect width="20" height="20" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  ),
  convert: (
    <path d="M8 3 4 7l4 4M4 7h16M16 21l4-4-4-4M20 17H4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  ),
  grid: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.75" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.75" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.75" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.75" />
    </>
  ),
  sparkles: (
    <path d="M12 3l1.6 4.9a2 2 0 0 0 1.3 1.3L20 11l-5.1 1.6a2 2 0 0 0-1.3 1.3L12 19l-1.6-4.9a2 2 0 0 0-1.3-1.3L4 11l4.9-1.6a2 2 0 0 0 1.3-1.3z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  ),
  results: (
    <>
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="9" y="3" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.75" />
      <path d="M9 13l2 2 4-4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  users: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.75" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  building: (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10.8346 9.16667H14.8346C15.7681 9.16667 16.2348 9.16667 16.5913 9.34832C16.9049 9.50811 17.1599 9.76308 17.3196 10.0767C17.5013 10.4332 17.5013 10.8999 17.5013 11.8333V17.5M10.8346 17.5V5.16667C10.8346 4.23325 10.8346 3.76654 10.653 3.41002C10.4932 3.09641 10.2382 2.84144 9.92462 2.68166C9.5681 2.5 9.10139 2.5 8.16797 2.5H5.16797C4.23455 2.5 3.76784 2.5 3.41132 2.68166C3.09771 2.84144 2.84275 3.09641 2.68296 3.41002C2.5013 3.76654 2.5013 4.23325 2.5013 5.16667V17.5M18.3346 17.5H1.66797M5.41797 5.83333H7.91797M5.41797 9.16667H7.91797M5.41797 12.5H7.91797" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  ),
  lock: (
    <>
      <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.75" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </>
  ),
  bars: <path d="M12 20V10M18 20V4M6 20v-4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />,
  message: (
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  ),
  filetext: (
    <>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
      <path d="M14 2v5h5M9 13h6M9 17h6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </>
  ),
  sliders: (
    <>
      <path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h7M15 18h5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <circle cx="16" cy="6" r="2" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="8" cy="12" r="2" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="13" cy="18" r="2" stroke="currentColor" strokeWidth="1.75" />
    </>
  ),
  warning: (
    <>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
      <path d="M12 9v4M12 17h.01" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </>
  ),
  summarize: (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M14 11H8M10 15H8M16 7H8M20 6.8V17.2C20 18.8802 20 19.7202 19.673 20.362C19.3854 20.9265 18.9265 21.3854 18.362 21.673C17.7202 22 16.8802 22 15.2 22H8.8C7.11984 22 6.27976 22 5.63803 21.673C5.07354 21.3854 4.6146 20.9265 4.32698 20.362C4 19.7202 4 18.8802 4 17.2V6.8C4 5.11984 4 4.27976 4.32698 3.63803C4.6146 3.07354 5.07354 2.6146 5.63803 2.32698C6.27976 2 7.11984 2 8.8 2H15.2C16.8802 2 17.7202 2 18.362 2.32698C18.9265 2.6146 19.3854 3.07354 19.673 3.63803C20 4.27976 20 5.11984 20 6.8Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  ),
  find: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <circle cx="11" cy="14" r="2.4" stroke="currentColor" strokeWidth="1.7" />
      <path d="m14.4 17.4 1.6 1.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </>
  ),
  qa: (
    <>
      <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5 8.5 8.5 0 0 1 21 11.5z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 9a2 2 0 1 1 3 1.7c-.6.4-1 .8-1 1.8M12 16h.01" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </>
  ),
  factcheck: (
    <path d="M9 11l2 2 4-4M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  ),
  compare: (
    <path d="M16 3l4 4-4 4M8 21l-4-4 4-4M20 7H8M4 17h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  ),
  swap: (
    <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.9" />
      <path d="m21 21-4.3-4.3" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </>
  ),
  download: (
    <>
      <path d="M12 15L12 5M12 15C11.2998 15 9.99153 13.0057 9.5 12.5M12 15C12.7002 15 14.0085 13.0057 14.5 12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M5 19H19.0001" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </>
  ),
  trash: (
  <path d="M9 3H15M3 6H21M19 6L18.2987 16.5193C18.1935 18.0975 18.1409 18.8867 17.8 19.485C17.4999 20.0118 17.0472 20.4353 16.5017 20.6997C15.882 21 15.0911 21 13.5093 21H10.4907C8.90891 21 8.11803 21 7.49834 20.6997C6.95276 20.4353 6.50009 20.0118 6.19998 19.485C5.85911 18.8867 5.8065 18.0975 5.70129 16.5193L5 6M10 10.5V15.5M14 10.5V15.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  ),
  close: <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />,
  refresh: (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M16.5 7.5C16.5 7.5 14.9963 5.45116 13.7746 4.22868C12.553 3.0062 10.8648 2.25 9 2.25C5.27208 2.25 2.25 5.27208 2.25 9C2.25 12.7279 5.27208 15.75 9 15.75C12.0773 15.75 14.6737 13.6907 15.4862 10.875M12 7.5H16.5V3" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  ),
  clock: (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 17.0625C4.5525 17.0625 0.9375 13.4475 0.9375 9C0.9375 4.5525 4.5525 0.9375 9 0.9375C13.4475 0.9375 17.0625 4.5525 17.0625 9C17.0625 13.4475 13.4475 17.0625 9 17.0625ZM9 2.0625C5.175 2.0625 2.0625 5.175 2.0625 9C2.0625 12.825 5.175 15.9375 9 15.9375C12.825 15.9375 15.9375 12.825 15.9375 9C15.9375 5.175 12.825 2.0625 9 2.0625Z" fill="currentColor"/>
      <path d="M11.783 11.9478C11.6855 11.9478 11.588 11.9253 11.498 11.8653L9.17297 10.4778C8.59547 10.1328 8.16797 9.37531 8.16797 8.70781V5.63281C8.16797 5.32531 8.42297 5.07031 8.73047 5.07031C9.03797 5.07031 9.29297 5.32531 9.29297 5.63281V8.70781C9.29297 8.97781 9.51797 9.37531 9.75047 9.51031L12.0755 10.8978C12.3455 11.0553 12.428 11.4003 12.2705 11.6703C12.158 11.8503 11.9705 11.9478 11.783 11.9478Z" fill="#575656"/>
    </svg>
  ),
  plus: <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />,
  chevronDown: <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />,
  chevronRight: <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />,
  arrowLeft: (
    <path d="M19 12H5m0 0l7 7m-7-7l7-7" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  ),
  arrowRight: (
    <path d="M5 12h14m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  arrowUpRight: (
    <path d="M7 17 17 7M8 7h9v9" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  ),
  check: <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />,
  checkCircle: (
    <path d="M9 11l2 2 4-4M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  ),
  upload: (
    <path d="M12 16V4m0 0L8 8m4-4l4 4M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  ),
  edit: (
    <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.75" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.75" />
      <path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </>
  ),
  play: <path d="M5 3l14 9-14 9V3z" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />,
  logout: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m16 17 5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  folder: (
    <path d="M4 7V5a2 2 0 0 1 2-2h3l2 2h7a2 2 0 0 1 2 2v0M3 9h18l-1.5 9a2 2 0 0 1-2 1.7H6.5a2 2 0 0 1-2-1.7z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  ),
  user: (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.85" />
    </>
  ),
  calendar: (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M6.66797 1.66602V4.99935" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.332 1.66602V4.99935" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15.8333 3.33398H4.16667C3.24619 3.33398 2.5 4.08018 2.5 5.00065V16.6673C2.5 17.5878 3.24619 18.334 4.16667 18.334H15.8333C16.7538 18.334 17.5 17.5878 17.5 16.6673V5.00065C17.5 4.08018 16.7538 3.33398 15.8333 3.33398Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.5 8.33398H17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6.66797 11.666H6.6763" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 11.666H10.0083" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.332 11.666H13.3404" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6.66797 15H6.6763" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 15H10.0083" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.332 15H13.3404" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  language: (
    <path d="M5 8h7M9 4v4m1.5 8L14 8l3.5 8M11 16h6M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  ),
  receipt: (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M3.33203 6.5C3.33203 5.09987 3.33203 4.3998 3.60451 3.86502C3.8442 3.39462 4.22665 3.01217 4.69705 2.77248C5.23183 2.5 5.9319 2.5 7.33203 2.5H12.6654C14.0655 2.5 14.7656 2.5 15.3003 2.77248C15.7707 3.01217 16.1532 3.39462 16.3929 3.86502C16.6654 4.3998 16.6654 5.09987 16.6654 6.5V17.5L14.3737 15.8333L12.2904 17.5L9.9987 15.8333L7.70703 17.5L5.6237 15.8333L3.33203 17.5V6.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  ),
  // Ported 1:1 from Figma exports; non-square native viewBoxes (16x20, 18x22)
  // are re-centered into the shared 24x24 box via a group transform so they
  // share this component's fixed viewBox instead of a bespoke one.
  fileConvert: (
    <g transform="translate(2.4 0) scale(1.2)">
      <path
        d="M4.75 14.75H10.75M4.75 11.75H10.75M8.75038 0.750872C8.65484 0.75 8.54738 0.75 8.42471 0.75H3.9502C2.83009 0.75 2.26962 0.75 1.8418 0.967988C1.46547 1.15973 1.15973 1.46547 0.967987 1.8418C0.75 2.26962 0.75 2.83009 0.75 3.9502V15.5502C0.75 16.6703 0.75 17.2301 0.967988 17.6579C1.15973 18.0342 1.46547 18.3405 1.8418 18.5322C2.26921 18.75 2.829 18.75 3.94694 18.75L11.5531 18.75C12.671 18.75 13.23 18.75 13.6574 18.5322C14.0337 18.3405 14.3405 18.0342 14.5322 17.6579C14.75 17.2305 14.75 16.6715 14.75 15.5536V7.07568C14.75 6.95302 14.7499 6.84553 14.749 6.75M8.75038 0.750872C9.03583 0.753475 9.21572 0.764066 9.38818 0.80547C9.59225 0.854464 9.78785 0.935265 9.9668 1.04492C10.1686 1.16857 10.3418 1.34181 10.6875 1.6875L13.813 4.81298C14.1589 5.15889 14.3309 5.33136 14.4546 5.53319C14.5642 5.71214 14.6453 5.90726 14.6943 6.11133C14.7357 6.28379 14.7464 6.46454 14.749 6.75M8.75038 0.750872L8.75 3.55021C8.75 4.67031 8.75 5.23015 8.96799 5.65797C9.15973 6.0343 9.46547 6.34048 9.8418 6.53223C10.2692 6.75 10.829 6.75 11.9469 6.75H14.749"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  ),
  alertTriangle: (
    <>
      <path
        d="M5.32171 9.68293C7.73539 5.41199 8.94222 3.27651 10.5983 2.72681C11.5093 2.4244 12.4907 2.4244 13.4017 2.72681C15.0578 3.27651 16.2646 5.41199 18.6783 9.68293C21.092 13.9539 22.2988 16.0893 21.9368 17.8293C21.7376 18.7866 21.2469 19.6549 20.535 20.3097C19.241 21.5 16.8274 21.5 12 21.5C7.17265 21.5 4.75897 21.5 3.46496 20.3097C2.75308 19.6549 2.26239 18.7866 2.06322 17.8293C1.70119 16.0893 2.90803 13.9539 5.32171 9.68293Z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path d="M11.992 16H12.001" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 13L12 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  convert02: (
    <path
      d="M16 8H20C20.5523 8 21 8.44772 21 9V20L17.667 17.231C17.4875 17.0818 17.2608 17 17.0273 17H9C8.44771 17 8 16.5523 8 16V13M16 8V5C16 4.44772 15.5523 4 15 4H4C3.44772 4 3 4.44772 3 5V16.0003L6.33301 13.2308C6.51255 13.0817 6.73924 13 6.97266 13H8M16 8V12C16 12.5523 15.5523 13 15 13H8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  fileCheck02: (
    <path
      d="M20 12.5V6.8C20 5.11984 20 4.27976 19.673 3.63803C19.3854 3.07354 18.9265 2.6146 18.362 2.32698C17.7202 2 16.8802 2 15.2 2H8.8C7.11984 2 6.27976 2 5.63803 2.32698C5.07354 2.6146 4.6146 3.07354 4.32698 3.63803C4 4.27976 4 5.11984 4 6.8V17.2C4 18.8802 4 19.7202 4.32698 20.362C4.6146 20.9265 5.07354 21.3854 5.63803 21.673C6.27976 22 7.11984 22 8.8 22H12M14 11H8M10 15H8M16 7H8M14.5 19L16.5 21L21 16.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  fileList: (
    <g transform="translate(2.1818 0) scale(1.0909)">
      <path
        d="M10.75 9.75H4.75M6.75 13.75H4.75M12.75 5.75H4.75M16.75 5.55V15.95C16.75 17.6302 16.75 18.4702 16.423 19.112C16.1354 19.6765 15.6765 20.1354 15.112 20.423C14.4702 20.75 13.6302 20.75 11.95 20.75H5.55C3.86984 20.75 3.02976 20.75 2.38803 20.423C1.82354 20.1354 1.3646 19.6765 1.07698 19.112C0.75 18.4702 0.75 17.6302 0.75 15.95V5.55C0.75 3.86984 0.75 3.02976 1.07698 2.38803C1.3646 1.82354 1.82354 1.3646 2.38803 1.07698C3.02976 0.75 3.86984 0.75 5.55 0.75H11.95C13.6302 0.75 14.4702 0.75 15.112 1.07698C15.6765 1.3646 16.1354 1.82354 16.423 2.38803C16.75 3.02976 16.75 3.86984 16.75 5.55Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  )
};

export interface IconProps {
  name: IconName;
  size?: number;
  style?: CSSProperties;
}

export function Icon({ name, size = 20, style }: IconProps) {
  const content = PATHS[name];
  if (isValidElement(content) && content.type === "svg") {
    return cloneElement(content as ReactElement<{ width?: number; height?: number; style?: CSSProperties }>, {
      width: size,
      height: size,
      style,
    });
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={style}>
      {content}
    </svg>
  );
}

/** Coloured document-type icons (36x36), as in the design. */
export type DocKind = "pdf" | "word" | "excel" | "generic";

export function DocIcon({ kind, size = 34 }: { kind: DocKind; size?: number }) {
  const theme = useTheme();
  const { palette } = theme;
  const map: Record<DocKind, { tab: string; body: string; label: string; fg: string }> = {
    pdf: {
      tab: alpha(palette.status.failFg, 0.28),
      body: palette.status.failBg,
      label: "PDF",
      fg: palette.status.failFg,
    },
    word: {
      tab: alpha(palette.info.main, 0.28),
      body: alpha(palette.info.main, 0.1),
      label: "DOC",
      fg: palette.brand.navy,
    },
    excel: {
      tab: alpha(palette.status.successFg, 0.28),
      body: palette.status.successBg,
      label: "XLS",
      fg: palette.status.successFg,
    },
    generic: {
      tab: palette.border.strong,
      body: palette.background.neutral,
      label: "DOC",
      fg: palette.text.secondary,
    },
  };
  const c = map[kind];
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" fill="none">
      <rect x="6" y="3" width="24" height="30" rx="3" fill={c.body} />
      <path d="M22 3l8 8h-8V3z" fill={c.tab} />
      <text x="18" y="25" textAnchor="middle" fontFamily="Hanken Grotesk,sans-serif" fontSize="8" fontWeight="800" fill={c.fg}>
        {c.label}
      </text>
    </svg>
  );
}

/** The real per-type file glyph used in document tiles, falling back to the
 *  generic coloured badge above for kinds without a dedicated icon. */
export function FileTypeIcon({ kind, size = 40 }: { kind: DocKind; size?: number }) {
  if (kind === "pdf") return <PdfIcon size={size} />;
  if (kind === "word") return <WordIcon size={size} />;
  if (kind === "excel") return <ExcelIcon size={size} />;
  return <DocIcon kind={kind} size={size} />;
}

/**
 * The bare file name of a path/URL: strips any directory, query string and
 * hash, and URL-decodes what is left. Never throws on malformed input.
 */
function baseName(raw: string): string {
  const withoutQuery = raw.split(/[?#]/)[0];
  const last = withoutQuery.split(/[\\/]/).pop() || "";
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}

/** The lower-cased extension (without the dot) of a file name / URL, or "". */
function extensionOf(raw?: string): string {
  const name = baseName((raw || "").trim());
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return "";
  return name.slice(dot + 1).toLowerCase();
}

/**
 * Map a file name / mime to a DocKind for the coloured icon.
 *
 * The file EXTENSION decides — it is the only thing that reliably describes the
 * real file type. `name` may be a plain title, a path or a full URL with a
 * query suffix (SAS tokens etc.) and may contain any number of dots; only the
 * final extension of the final path segment counts, case-insensitively. The
 * mime type is a fallback, and anything unrecognised gets the neutral icon
 * rather than being mislabelled as a Word document (UI/UX audit 2.3.4).
 */
export function docKindFor(name?: string, mime?: string): DocKind {
  const ext = extensionOf(name);
  if (ext === "pdf") return "pdf";
  if (ext === "doc" || ext === "docx") return "word";
  if (ext === "xls" || ext === "xlsx") return "excel";

  const m = (mime || "").toLowerCase();
  if (m) {
    if (m.includes("pdf")) return "pdf";
    if (m.includes("word") || m.includes("msword") || m.includes("wordprocessing")) return "word";
    if (m.includes("sheet") || m.includes("excel") || m.includes("spreadsheet")) return "excel";
  }
  return "generic";
}

/** A trailing GUID/UUID that our storage layer appends to uploaded file names. */
const TRAILING_UUID =
  /[-_\s(]*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\)?$/i;

/**
 * The human-readable form of a stored file name: no directories, no query
 * string and no machine-generated UUID suffix — just the name the user gave the
 * file plus its extension (UI/UX audit 2.3.4 / 2.4.5).
 *
 * Presentation only: the real name (`tittle` / `documentLink`) must still be
 * used for downloads and for anything sent to the API.
 */
export function displayFileName(raw?: string): string {
  if (!raw) return "";
  const name = baseName(raw.trim());
  if (!name) return "";

  const dot = name.lastIndexOf(".");
  const hasExt = dot > 0 && dot < name.length - 1;
  const stem = hasExt ? name.slice(0, dot) : name;
  const ext = hasExt ? name.slice(dot) : "";

  const cleaned = stem.replace(TRAILING_UUID, "").trim();
  return `${cleaned || stem}${ext}`;
}
